<template>
  <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <ComponentHeader title="User Activity">
      <div class="flex items-center gap-3">
        <span v-if="exporting" class="text-xs text-gray-500">Preparing export…</span>
        <span v-else-if="exportError" class="text-xs text-red-600">{{ exportError }}</span>
        <ExportMenu :exports="[{ type: 'excel', action: exportToExcel, loading: exporting }]" />
      </div>
    </ComponentHeader>

    <!-- Filters -->
    <div class="px-6 py-4 flex items-center flex-wrap gap-4">
      <FilterDropdown
        label="User"
        v-model="selectedUser"
        :options="userOptions"
        :sensitive-labels="true"
        placeholder="All Users"
        clearable
        @change="refreshData"
      />
      <FilterDropdown
        label="Event Type"
        v-model="selectedEventType"
        :options="eventTypeOptions"
        :sensitive-labels="false"
        placeholder="All Events"
        clearable
        @change="refreshData"
      />
      <FilterDropdown
        label="Category"
        v-model="selectedCategory"
        :options="categoryOptions"
        :sensitive-labels="false"
        placeholder="All Categories"
        clearable
        @change="refreshData"
      />
      <FilterDropdown
        label="HTTP Method"
        v-model="selectedMethod"
        :options="methodOptions"
        :sensitive-labels="false"
        placeholder="All Methods"
        clearable
        @change="refreshData"
      />
      <div class="inline-flex flex-col gap-0.5">
        <label class="text-xs font-semibold">Date From</label>
        <input
          v-model="dateFrom"
          type="datetime-local"
          class="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          @change="refreshData"
        />
      </div>
      <div class="inline-flex flex-col gap-0.5">
        <label class="text-xs font-semibold">Date To</label>
        <input
          v-model="dateTo"
          type="datetime-local"
          class="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          @change="refreshData"
        />
      </div>
      <div class="flex-1 min-w-[200px] max-w-[400px]">
        <label class="text-xs font-semibold block mb-0.5">Search</label>
        <input
          v-model="searchText"
          type="text"
          placeholder="Search URLs, payloads, routes..."
          class="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          @keydown.enter="refreshData"
        />
      </div>
    </div>

    <!-- Grid -->
    <div class="relative" style="height: 700px">
      <AgGridVue
        :theme="standardListTheme"
        style="width: 100%; height: 100%"
        :column-defs="columnDefs"
        :default-col-def="defaultColDef"
        :grid-options="gridOptions"
        :loading="loading"
        :get-row-id="getRowId"
        :row-model-type="'serverSide'"
        @grid-ready="onGridReady"
      />
    </div>

    <!-- Payload Detail Modal -->
    <Teleport to="body">
      <JSON_Modal
        v-if="detailModal.visible"
        :json-data="detailModal.data"
        :title="detailModal.title"
        @close="detailModal.visible = false"
      />
    </Teleport>
  </div>
</template>

<script>
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import { standardListTheme } from '@/utils/common';
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue';

import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import JSON_Modal from '@/components/widgetcomponents/JSON_Modal.vue';

export default {
  name: 'UserActivityTable',
  components: {
    AgGridVue,
    FilterDropdown,
    ComponentHeader,
    ExportMenu,
    JSON_Modal,
  },
  data() {
    return {
      gridApi: null,
      gridOptions: {
        rowModelType: 'serverSide',
        serverSideStoreType: 'partial',
        cacheBlockSize: 50,
        maxBlocksInCache: 3,
        maxConcurrentDatasourceRequests: 1,
        blockLoadDebounceMillis: 200,
        animateRows: true,
      },
      loading: true,
      defaultColDef: {
        flex: 1,
        sortable: true,
        resizable: true,
        cellClass: 'cell-theme-left-aligned',
      },
      exporting: false,
      exportError: null,
      selectedUser: null,
      selectedEventType: null,
      selectedCategory: null,
      selectedMethod: null,
      dateFrom: (() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().slice(0, 16);
      })(),
      dateTo: '',
      searchText: '',
      userOptions: [],
      eventTypeOptions: [
        { value: 'api', label: 'API Calls' },
        { value: 'page_view', label: 'Page Views' },
        { value: 'click', label: 'Clicks' },
        { value: 'session_start', label: 'Sessions' },
      ],
      categoryOptions: [
        { value: 'Authentication', label: 'Authentication' },
        { value: 'Security Admin', label: 'Security Admin' },
        { value: 'User Management', label: 'User Management' },
        { value: 'Data Update', label: 'Data Update' },
        { value: 'Data Delete', label: 'Data Delete' },
        { value: 'Data Access', label: 'Data Access' },
      ],
      methodOptions: [
        { value: 'GET', label: 'GET' },
        { value: 'POST', label: 'POST' },
        { value: 'PUT', label: 'PUT' },
        { value: 'PATCH', label: 'PATCH' },
        { value: 'DELETE', label: 'DELETE' },
      ],

      detailModal: {
        visible: false,
        data: null,
        title: '',
      },
    };
  },
  computed: {
    standardListTheme() {
      return standardListTheme;
    },
    columnDefs() {
      return [
        {
          headerName: 'Timestamp',
          field: 'CLIENT_TIMESTAMP',
          sort: 'desc',
          minWidth: 170,
          maxWidth: 190,
          valueFormatter: params => {
            if (!params.value) return '';
            const d = new Date(params.value);
            return d.toLocaleString();
          },
        },
        {
          headerName: 'User',
          field: 'USER_NAME',
          cellClass: 'cell-theme-left-aligned sensitive',
          minWidth: 130,
          maxWidth: 180,
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Category',
          field: 'EVENT_CATEGORY',
          minWidth: 120,
          maxWidth: 150,
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Method',
          field: 'METHOD',
          minWidth: 75,
          maxWidth: 90,
          cellClass: params => {
            const m = params.value;
            if (m === 'POST' || m === 'PUT' || m === 'PATCH') return 'text-amber-700 font-medium';
            if (m === 'DELETE') return 'text-red-600 font-medium';
            return 'text-gray-500';
          },
        },
        {
          headerName: 'URL',
          field: 'URL',
          minWidth: 250,
          flex: 3,
          filter: 'agTextColumnFilter',
          tooltipField: 'URL',
        },
        {
          headerName: 'Status',
          field: 'STATUS_CODE',
          minWidth: 70,
          maxWidth: 85,
          cellClass: params => {
            if (params.value >= 400) return 'text-red-600 font-medium';
            if (params.value >= 200 && params.value < 300) return 'text-green-600';
            return '';
          },
        },

        {
          headerName: 'Entity',
          field: 'ENTITY_NAME',
          cellClass: 'cell-theme-left-aligned sensitive',
          minWidth: 140,
          maxWidth: 220,
          filter: 'agTextColumnFilter',
          valueFormatter: params => params.value || '—',
        },
        {
          headerName: 'Page',
          field: 'PAGE_ROUTE',
          minWidth: 130,
          maxWidth: 200,
          filter: 'agTextColumnFilter',
          tooltipField: 'PAGE_URL',
        },
        {
          headerName: 'Payload',
          field: 'REQUEST_PAYLOAD',
          minWidth: 100,
          maxWidth: 120,
          sortable: false,
          cellRenderer: params => {
            if (!params.value) return '<span class="text-gray-300">—</span>';
            return '<a href="#" class="text-primary-600 underline hover:text-primary-800">View</a>';
          },
          onCellClicked: params => {
            if (params.value) {
              this.detailModal = {
                visible: true,
                data: params.value,
                title: `${params.data.METHOD} ${params.data.URL}`,
              };
            }
          },
        },
        {
          headerName: 'Type',
          field: 'EVENT_TYPE',
          minWidth: 90,
          maxWidth: 110,
        },
      ];
    },
  },
  methods: {
    getRowId(params) {
      return String(params.data.ID);
    },
    async onGridReady(params) {
      this.gridApi = params.api;
      await this.loadUsers();

      const datasource = {
        getRows: async (paramsRequest) => {
          this.loading = true;
          this.gridApi.setGridOption('loading', true);

          try {
            const payload = {
              start_row: paramsRequest.request.startRow,
              end_row: paramsRequest.request.endRow,
              sort_model: paramsRequest.request.sortModel || [],
              filter_model: paramsRequest.request.filterModel || {},
              ...this.buildFilterPayload(),
            };

            const response = await axios.post('api/tracking/user-activity/list/', payload);

            if (response.data?.rowData) {
              paramsRequest.success({
                rowData: response.data.rowData,
                rowCount: response.data.lastRow,
              });
            } else {
              paramsRequest.success({ rowData: [], rowCount: 0 });
            }

            params.api.sizeColumnsToFit();
          } catch (error) {
            console.error('Error fetching user activity:', error);
            paramsRequest.fail();
          } finally {
            this.loading = false;
            this.gridApi.setGridOption('loading', false);
          }
        },
      };

      this.gridApi.setGridOption('serverSideDatasource', datasource);
      window.addEventListener('resize', this.onWindowResize);
    },
    onWindowResize() {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    },
    async loadUsers() {
      try {
        const response = await axios.get('api/tracking/user-activity/users/');
        this.userOptions = response.data;
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },
    buildFilterPayload() {
      const payload = {};

      // Event types — 'api' maps to both api_call and api_error; null = all types
      if (this.selectedEventType) {
        payload.event_types = this.selectedEventType === 'api'
          ? ['api_call', 'api_error']
          : [this.selectedEventType];
      }

      // Date range
      if (this.dateFrom) {
        payload.date_from = new Date(this.dateFrom).toISOString();
      }
      if (this.dateTo) {
        payload.date_to = new Date(this.dateTo).toISOString();
      }

      // User
      if (this.selectedUser) {
        payload.user_id = this.selectedUser;
      }

      // Category
      if (this.selectedCategory) {
        payload.event_category = this.selectedCategory;
      }

      // HTTP method
      if (this.selectedMethod) {
        payload.methods = [this.selectedMethod];
      }

      // Search
      if (this.searchText.trim()) {
        payload.search = this.searchText.trim();
      }

      return payload;
    },
    async exportToExcel() {
      if (this.exporting) return;
      this.exporting = true;
      this.exportError = null;

      try {
        // Async export: the backend enqueues a job that builds the CSV in the
        // tasks worker and uploads it to S3 (off the request path — no proxy
        // timeout on large forensic pulls). We poll the job, then download via
        // a short-lived presigned S3 URL (bytes go S3→browser directly).
        const { data: enqueued } = await axios.post('api/tracking/user-activity/list/', {
          export: true,
          sort_model: [{ colId: 'client_timestamp', sort: 'desc' }],
          ...this.buildFilterPayload(),
        });

        const jobId = enqueued.job_id;
        const result = await this.pollExportJob(jobId);
        if (result === 'failed') {
          throw new Error('The audit export could not be generated.');
        }

        const { data: dl } = await axios.get(`api/tracking/user-activity/export/${jobId}/`);
        // The presigned URL points straight at S3; a plain anchor navigation
        // triggers the download with the attachment filename baked into the URL.
        const link = document.createElement('a');
        link.href = dl.download_url;
        link.download = dl.filename || 'user_activity.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error exporting user activity:', error);
        this.exportError = 'Export failed. Please try again or narrow the filters.';
      } finally {
        this.exporting = false;
      }
    },
    async pollExportJob(jobId) {
      // Poll every 2s up to ~5 minutes. The export runs in the tasks worker,
      // so this outlives any single HTTP request budget. Stops early when
      // Demo Mode starts, since the result could not be downloaded anyway.
      const maxAttempts = 150;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const { data } = await axios.get(`api/jobs/${jobId}/status/`);
        if (data.status === 'completed') return 'completed';
        if (data.status === 'failed') return 'failed';
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      return 'failed';
    },
    refreshData() {
      if (this.gridApi) {
        this.gridApi.refreshServerSide({ purge: true });
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  },
};
</script>
